/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { DRAG_DROP_PASTE } from '@lexical/rich-text';
import { isMimeType, mediaFileReader, mergeRegister } from '@lexical/utils';
import { COMMAND_PRIORITY_LOW, COMMAND_PRIORITY_NORMAL, DROP_COMMAND } from 'lexical';
import { useEffect } from 'react';

import { INSERT_IMAGE_COMMAND } from './ImagesPlugin';

const ACCEPTABLE_IMAGE_TYPES = [
  'image/',
  'image/heic',
  'image/heif',
  'image/gif',
  'image/webp',
];

export default function DragDropPaste(): null {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    return mergeRegister(
      // 기존: 로컬 파일 드래그앤드롭/붙여넣기 처리
      editor.registerCommand(
        DRAG_DROP_PASTE,
        (files) => {
          (async () => {
            const filesResult = await mediaFileReader(
              files,
              [ACCEPTABLE_IMAGE_TYPES].flatMap((x) => x)
            );
            for (const { file, result } of filesResult) {
              if (isMimeType(file, ACCEPTABLE_IMAGE_TYPES)) {
                editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
                  altText: file.name,
                  src: result,
                });
              }
            }
          })();
          return true;
        },
        COMMAND_PRIORITY_LOW
      ),
      // 추가: 외부 이미지 URL 드롭 처리 (채팅창 이미지 등)
      editor.registerCommand<DragEvent>(
        DROP_COMMAND,
        (event) => {
          const dataTransfer = event.dataTransfer;
          if (!dataTransfer) {
            return false;
          }

          // application/x-lexical-drag 데이터 확인 (채팅창 이미지)
          const lexicalData = dataTransfer.getData('application/x-lexical-drag');
          if (lexicalData) {
            try {
              const parsed = JSON.parse(lexicalData);
              if (parsed.type === 'image' && parsed.data?.src) {
                event.preventDefault();
                editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
                  src: parsed.data.src,
                  altText: parsed.data.altText || 'AI 채팅 이미지',
                });
                return true;
              }
            } catch (e) {
              // JSON 파싱 실패 시 무시
            }
          }

          // text/uri-list에서 이미지 URL 확인
          const uriList = dataTransfer.getData('text/uri-list');
          if (uriList) {
            const urls = uriList.split('\n').filter(url => url.trim() && !url.startsWith('#'));
            for (const url of urls) {
              // 이미지 URL인지 확인 (확장자 기반)
              if (/\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?.*)?$/i.test(url)) {
                event.preventDefault();
                editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
                  src: url.trim(),
                  altText: '드롭된 이미지',
                });
                return true;
              }
            }
          }

          return false; // 다른 핸들러에 위임
        },
        COMMAND_PRIORITY_NORMAL // ImagesPlugin(HIGH)보다 낮게, 기본 드롭보다 높게
      )
    );
  }, [editor]);
  return null;
}
